import { signIn, signOut, getCurrentUser, fetchUserAttributes, type SignInInput } from 'aws-amplify/auth';
import { judgeService } from './judgeService';

export interface User {
  id: string;
  email: string;
  groups: string[];
  fullName?: string;
  isAuthenticated: boolean;
  assignedCategories?: string[]; // Categories assigned to judges
}

export interface LoginCredentials {
  email: string;
  password: string;
}

// Define Cognito user groups and their permissions
export const USER_GROUPS = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  JUDGE: 'JUDGE',
} as const;

export const GROUP_PERMISSIONS = {
  [USER_GROUPS.SUPER_ADMIN]: {
    canManageUsers: true,
    canManageApplications: true,
    canViewAllData: true,
    canManageSystem: true,
  },
  [USER_GROUPS.ADMIN]: {
    canManageUsers: false,
    canManageApplications: true,
    canViewAllData: true,
    canManageSystem: false,
  },
  [USER_GROUPS.JUDGE]: {
    canManageUsers: false,
    canManageApplications: false,
    canViewAllData: false,
    canManageSystem: false,
  },
};

// Helper function to get the highest permission level from user groups
const getHighestPermissionGroup = (groups: string[]): string => {
  if (groups.includes(USER_GROUPS.SUPER_ADMIN)) {
    return USER_GROUPS.SUPER_ADMIN;
  }
  if (groups.includes(USER_GROUPS.ADMIN)) {
    return USER_GROUPS.ADMIN;
  }
  if (groups.includes(USER_GROUPS.JUDGE)) {
    return USER_GROUPS.JUDGE;
  }
  return USER_GROUPS.JUDGE; // Default fallback
};

class AuthService {
  private currentUser: User | null = null;

  async login(credentials: LoginCredentials): Promise<User> {
    try {

      // Check if user is already authenticated
      try {
        const currentUser = await this.getCurrentUser();
        this.currentUser = currentUser;
        return currentUser;
      } catch (error) {
        // User is not authenticated, proceed with login
      }

      const signInInput: SignInInput = {
        username: credentials.email,
        password: credentials.password,
      };

      const signInOutput = await signIn(signInInput);

      // Check if we need to handle a password challenge
      if (signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        const error = new Error('New password required') as Error & { name: string };
        error.name = 'NewPasswordRequiredException';
        throw error;
      }

      if (signInOutput.isSignedIn) {
        // Add a small delay to ensure the session is fully established
        await new Promise(resolve => setTimeout(resolve, 1000));

        const user = await this.getCurrentUser();
        this.currentUser = user;
        return user;
      } else {
        throw new Error('Sign in was not successful');
      }
    } catch (error) {
      console.error('Login error:', error);

      // Don't try to get current user if it's a password challenge
      if (error && typeof error === 'object' && 'name' in error && error.name === 'NewPasswordRequiredException') {
        throw error; // Let the UI handle this
      }

      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut();
      this.currentUser = null;
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const cognitoUser = await getCurrentUser();

      const attributes = await fetchUserAttributes();

      // Get user groups from Cognito
      let groups: string[] = [];

      // Try to get groups from custom attributes first
      const customGroups = attributes['custom:groups'] as string;
      if (customGroups) {
        groups = customGroups.split(',').map(g => g.trim());
      }

      // Also check for individual group attributes
      const isSuperAdmin = attributes['custom:isSuperAdmin'] === 'true';
      const isAdmin = attributes['custom:isAdmin'] === 'true';
      const isJudge = attributes['custom:isJudge'] === 'true';

      if (isSuperAdmin) {
        groups = [USER_GROUPS.SUPER_ADMIN];
      } else if (isAdmin) {
        groups = [USER_GROUPS.ADMIN];
      } else if (isJudge) {
        groups = [USER_GROUPS.JUDGE];
      }

      // If no groups found in custom attributes, try to get from JWT token
      if (groups.length === 0) {
        try {
          // Import fetchAuthSession to get the JWT token
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const authSession = await fetchAuthSession();
          const idToken = authSession.tokens?.idToken?.toString();

          if (idToken) {
            // Decode the JWT token to get group information
            const tokenParts = idToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));

              // Check for cognito:groups in the JWT token
              const cognitoGroups = payload['cognito:groups'];
              if (cognitoGroups && Array.isArray(cognitoGroups)) {
                groups = cognitoGroups.map((group: string) => {
                  // Map Cognito group names to our internal group names
                  switch (group) {
                    case 'SUPER_ADMIN':
                      return USER_GROUPS.SUPER_ADMIN;
                    case 'ADMIN':
                      return USER_GROUPS.ADMIN;
                    case 'JUDGE':
                      return USER_GROUPS.JUDGE;
                    default:
                      return group.toLowerCase();
                  }
                });
              }
            }
          }
        } catch (jwtError) {
          console.error('Error getting groups from JWT token:', jwtError);
        }
      }

      // If still no groups found, try to get from Cognito groups via GraphQL
      if (groups.length === 0) {
        try {
          // Get user's Cognito groups using the cognitoUserService
          const { cognitoUserService } = await import('./cognitoUserService');
          const userGroups = await cognitoUserService.getUserGroups(attributes.email || '');

          if (userGroups && userGroups.length > 0) {
            groups = userGroups;
          }
        } catch (error) {
          console.error('Error getting groups from Cognito:', error);
        }
      }

      const fullName = attributes['custom:fullName'] as string || attributes.name || '';

      // Get assigned categories for judges
      let assignedCategories: string[] = [];
      const customCategories = attributes['custom:assignedCategories'] as string;
      if (customCategories) {
        assignedCategories = customCategories.split(',').map(c => c.trim());
      }

      // No local testing overrides - use only Cognito data

      // Get assigned categories from judgeService for judges
      if (groups.includes(USER_GROUPS.JUDGE)) {
        try {
          const judgeAssignedCategories = await judgeService.getAssignedCategories(attributes.email || '');
          if (judgeAssignedCategories.length > 0) {
            assignedCategories = judgeAssignedCategories;
          }
        } catch (error) {
          console.error('Error getting assigned categories from judgeService:', error);
        }
      }

      const user: User = {
        id: cognitoUser.userId,
        email: attributes.email || '',
        groups,
        fullName,
        assignedCategories,
        isAuthenticated: true,
      };

      this.currentUser = user;
      return user;
    } catch (error) {
      console.error('Get current user error:', error);

      // If we can't get the current user, try to create a basic user object
      // This might happen if the user is authenticated but we can't fetch attributes
      try {
        await getCurrentUser();

        // No hardcoded overrides - user has no assigned role
        throw new Error('User has no assigned role in Cognito');
      } catch (fallbackError) {
        console.error('Fallback user creation failed:', fallbackError);
        throw error; // Throw the original error
      }
    }
  }

  async checkAuthStatus(): Promise<User | null> {
    try {
      const user = await this.getCurrentUser();
      return user;
    } catch (error) {
      console.error('Check auth status error:', error);
      return null;
    }
  }

  hasPermission(user: User, permission: keyof typeof GROUP_PERMISSIONS[typeof USER_GROUPS.SUPER_ADMIN]): boolean {
    const highestGroup = getHighestPermissionGroup(user.groups);
    const userPermissions = GROUP_PERMISSIONS[highestGroup as keyof typeof GROUP_PERMISSIONS];
    return userPermissions[permission] || false;
  }

  getUserHighestGroup(user: User): string {
    return getHighestPermissionGroup(user.groups);
  }

  isInGroup(user: User, group: string): boolean {
    const result = user.groups.includes(group);
    return result;
  }

  getCurrentUserFromCache(): User | null {
    return this.currentUser;
  }

  /**
   * Get user groups from Cognito using Admin API
   * This requires proper IAM permissions
   */
  async getUserGroupsFromCognito(): Promise<string[]> {
    try {
      // This would require AWS SDK and proper IAM permissions
      // For now, we'll return empty array and rely on custom attributes

      // In a real implementation, you would:
      // 1. Use AWS SDK to call adminListGroupsForUser
      // 2. Or use a Lambda function to get user groups
      // 3. Or use the custom attributes approach (which we're doing)

      return [];
    } catch (error) {
      console.error('Error getting groups from Cognito:', error);
      return [];
    }
  }
}

export const authService = new AuthService();
export default authService;